// ===========================================================================
// Simulation Canvas Component
// Main visualization canvas for the crowd/mob detection demo
// ===========================================================================

import React, { useRef, useEffect, useCallback } from 'react';
import {
  WorldState,
  FrameData,
  UIState,
  WORLD_WIDTH,
  WORLD_HEIGHT,
} from '../types';
import {
  drawPerson,
  drawWeapon,
  drawBoundingBox,
  drawTrackId,
  drawTrackHistory,
  drawContour,
  drawAlertBanner,
  renderGridOverlay,
  canvasToWorld,
} from '../utils/rendering';
import { findAgentAt } from '../sim/world';

interface SimulationCanvasProps {
  world: WorldState;
  frameData: FrameData | null;
  uiState: UIState;
  onAddPerson: (x: number, y: number) => void;
  onAddWeapon: (x: number, y: number) => void;
  onRemoveAgent: (id: string) => void;
  onSelectAgent: (id: string | null) => void;
  onMoveAgent: (id: string, x: number, y: number) => void;
  onDragStart: (id: string, offsetX: number, offsetY: number) => void;
  onDragEnd: () => void;
}

export const SimulationCanvas: React.FC<SimulationCanvasProps> = ({
  world,
  frameData,
  uiState,
  onAddPerson,
  onAddWeapon,
  onRemoveAgent,
  onSelectAgent,
  onMoveAgent,
  onDragStart,
  onDragEnd,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  // Get canvas dimensions
  const getCanvasDimensions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return { width: 1280, height: 720 };
    
    const rect = container.getBoundingClientRect();
    // Maintain 16:9 aspect ratio
    const aspectRatio = WORLD_WIDTH / WORLD_HEIGHT;
    let width = rect.width;
    let height = width / aspectRatio;
    
    if (height > rect.height) {
      height = rect.height;
      width = height * aspectRatio;
    }
    
    return { width, height };
  }, []);

  // Handle mouse events
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    const { x: worldX, y: worldY } = canvasToWorld(canvasX, canvasY, canvas.width, canvas.height);

    // Right click: delete
    if (e.button === 2) {
      e.preventDefault();
      const hit = findAgentAt(world, worldX, worldY, 30);
      if (hit) {
        onRemoveAgent(hit.agent.id);
      }
      return;
    }

    // Left click
    if (e.button === 0) {
      const hit = findAgentAt(world, worldX, worldY, 25);
      
      if (hit) {
        // Start dragging
        isDragging.current = true;
        onSelectAgent(hit.agent.id);
        onDragStart(hit.agent.id, worldX - hit.agent.x, worldY - hit.agent.y);
      } else if (e.shiftKey) {
        // Shift+click: add weapon
        onAddWeapon(worldX, worldY);
      } else {
        // Click on empty: add person
        onAddPerson(worldX, worldY);
      }
    }
  }, [world, onAddPerson, onAddWeapon, onRemoveAgent, onSelectAgent, onDragStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging.current || !world.selectedAgentId || !world.dragOffset) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    const { x: worldX, y: worldY } = canvasToWorld(canvasX, canvasY, canvas.width, canvas.height);

    onMoveAgent(
      world.selectedAgentId,
      worldX - world.dragOffset.x,
      worldY - world.dragOffset.y
    );
  }, [world.selectedAgentId, world.dragOffset, onMoveAgent]);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    onDragEnd();
  }, [onDragEnd]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Update canvas size
    const { width, height } = getCanvasDimensions();
    canvas.width = width;
    canvas.height = height;

    const scale = width / WORLD_WIDTH;

    // Clear canvas
    ctx.fillStyle = '#0f0f14';
    ctx.fillRect(0, 0, width, height);

    // Draw grid lines
    ctx.strokeStyle = '#1a1a24';
    ctx.lineWidth = 1;
    const gridSize = 80;
    for (let x = 0; x < WORLD_WIDTH; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x * scale, 0);
      ctx.lineTo(x * scale, height);
      ctx.stroke();
    }
    for (let y = 0; y < WORLD_HEIGHT; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y * scale);
      ctx.lineTo(width, y * scale);
      ctx.stroke();
    }

    // Draw depth overlay if enabled
    if (uiState.showDepthOverlay && frameData) {
      renderGridOverlay(ctx, frameData.depthGrid, 'grayscale', 0.3, width, height);
    }

    // Draw heatmap overlay if enabled
    if (uiState.showHeatmapOverlay && frameData) {
      renderGridOverlay(ctx, frameData.heatmapGrid, 'heat', 0.5, width, height);
    }

    // Draw contours if enabled
    if (uiState.showContours && frameData) {
      for (const region of frameData.crowdRegions) {
        drawContour(ctx, region, scale);
      }
    }

    // Draw track history if enabled
    if (uiState.showTrackHistory && frameData) {
      for (const track of frameData.tracks) {
        if (track.confirmed) {
          drawTrackHistory(ctx, track, scale);
        }
      }
    }

    // Draw bounding boxes if enabled
    if (uiState.showBoundingBoxes && frameData) {
      for (const det of frameData.detections) {
        drawBoundingBox(ctx, det, scale, true);
      }
    }

    // Draw people
    for (const person of world.people) {
      const track = frameData?.tracks.find(t => t.agentId === person.id);
      const isSuspect = track?.label === 'suspect' || person.isSuspect;
      const isSelected = world.selectedAgentId === person.id;
      drawPerson(ctx, person, isSuspect, isSelected, scale);
    }

    // Draw weapons
    for (const weapon of world.weapons) {
      const isSelected = world.selectedAgentId === weapon.id;
      drawWeapon(ctx, weapon, isSelected, scale);
    }

    // Draw track IDs if enabled
    if (uiState.showTrackIds && frameData) {
      for (const track of frameData.tracks) {
        if (track.confirmed) {
          drawTrackId(ctx, track, scale);
        }
      }
    }

    // Draw alert banners
    if (frameData) {
      if (frameData.flags.mob) {
        drawAlertBanner(ctx, 'mob', width);
      } else if (frameData.flags.crowd) {
        drawAlertBanner(ctx, 'crowd', width);
      }
      if (frameData.flags.weaponGlobal) {
        drawAlertBanner(ctx, 'weapon', width);
      }
    }

    // Draw frame info
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '12px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`Frame: ${world.frameIndex}`, 10, height - 10);
    ctx.fillText(`People: ${world.people.length}`, 100, height - 10);
    ctx.fillText(`Weapons: ${world.weapons.length}`, 200, height - 10);
    if (frameData) {
      ctx.fillText(`Tracks: ${frameData.metrics.confirmedTracks}`, 310, height - 10);
    }

    // Draw instructions
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fillText('Click: add person | Shift+Click: add weapon | Drag: move | Right-click: delete', width - 10, height - 10);

  }, [world, frameData, uiState, getCanvasDimensions]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { width, height } = getCanvasDimensions();
      canvas.width = width;
      canvas.height = height;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getCanvasDimensions]);

  return (
    <div 
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0a0f',
      }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onContextMenu={handleContextMenu}
        style={{
          cursor: isDragging.current ? 'grabbing' : 'crosshair',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        }}
      />
    </div>
  );
};
